import { pb } from './pocketbase';
import { createNotification } from './notification.service';
import eventBus, { EVENTS } from '../utils/eventBus';
import eventEmitter from './event.service';


const handleServiceError = (error, message) => {
    console.error(message, error);
    return {
        success: false,
        error: error.message || message,
    };
};

const getAuthenticatedUser = () => {
    const user = pb.authStore.model;
    if (!user || !user.partner_id) {
        throw new Error('Utilisateur non authentifié ou partner_id manquant.');
    }
    return user;
};

const createOffer = async (offerData) => {
    try {
        const user = getAuthenticatedUser();
        const company = await pb.collection('companies').getFirstListItem(`partner_id = "${user.partner_id}"`);

        const { job_description, ...otherData } = offerData;

        const offerToCreate = {
            ...otherData,
            partner_id: user.partner_id,
            company_name: company.name,
            status: 'Publiée',
        };

        if (job_description instanceof File) {
            offerToCreate.job_description = job_description;
        }

        const record = await pb.collection('alternance_offers').create(offerToCreate);
        eventEmitter.dispatch('offersUpdated');

        try {
            await createNotification({
                type: "offer_created",
                title: "Création d'une offre",
                message: `L'entreprise ${company.name} a créé une nouvelle offre d'alternance : "${record.title}"`,
                company_id: company.id,
                sender_type: "company",
                icon: "convention_logo.png",
                link: `/admin/companies/${company.id}`,
                read: false,
            });
        } catch (notificationError) {
            // Gérer silencieusement l'échec de la notification
            // car cela ne doit pas bloquer la création de l'offre.
            console.error("Erreur lors de la création de la notification:", notificationError);
        }

        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la création de l'offre.");
    }
};

// Récupérer les offres d'une entreprise spécifique
const getCompanyOffers = async (options = {}) => {
    try {
        const user = getAuthenticatedUser();
        const { page = 1, perPage = 10, sort = '-created', search = '', expand = 'assigned_to', contractTypes = [], statuses = [], sorting = '' } = options;

        let filter = `partner_id = "${user.partner_id}"`;

        const addFilterCondition = (array, field) => {
            if (array && array.length > 0) {
                const condition = array.map(value => `${field} = "${value}"`).join(' || ');
                filter += ` && (${condition})`;
            }
        };

        addFilterCondition(contractTypes, 'type_contrat');
        addFilterCondition(statuses, 'status');

        let finalSort = sort;
        switch (sorting) {
            case 'Plus récentes': finalSort = '-created'; break;
            case 'Plus anciennes': finalSort = 'created'; break;
            case 'Alphabétique': finalSort = 'title'; break;
            default: break;
        }

        const records = await pb.collection('alternance_offers').getList(page, perPage, {
            sort: finalSort,
            filter,
            expand: 'company_id,candidats_ids,assigned_to',
        });

        // Suppression de la logique `processedItems` car `records.items` est déjà le tableau que vous voulez
        // modifier et le renvoyer.
        return {
            success: true,
            data: records.items.map(offer => ({
                ...offer,
                company_name: offer.expand?.company_id?.name || offer.company_name || "Entreprise non définie"
            })),
            ...records,
            items: undefined // Supprime la propriété items du retour pour éviter la redondance
        };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la récupération des offres de l'entreprise.");
    }
};

// Récupérer une offre par ID
const getOfferById = async (id) => {
    try {
        const record = await pb.collection('alternance_offers').getOne(id, {
            expand: 'company_id,candidats_ids,assigned_to',
        });
        return { success: true, data: record };
    } catch (error) {
        if (error.status === 404) {
            return { success: false, error: "Offre d'alternance non trouvée." };
        }
        return handleServiceError(error, "Erreur lors de la récupération de l'offre.");
    }
};

// Mettre à jour une offre
const updateOffer = async (id, offerData) => {
    try {
        const record = await pb.collection('alternance_offers').update(id, offerData);
        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la mise à jour de l'offre.");
    }
};

// Supprimer une offre
const deleteOffer = async (id) => {
    try {
        await pb.collection('alternance_offers').delete(id);
        eventEmitter.dispatch('offersUpdated');
        return { success: true };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la suppression de l'offre.");
    }
};

// Supprimer plusieurs offres en lot
const deleteMultipleOffers = async (offerIds) => {
    try {
        await Promise.all(offerIds.map(id => pb.collection('alternance_offers').delete(id)));
        eventEmitter.dispatch('offersUpdated');
        return { success: true, deletedCount: offerIds.length };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la suppression des offres.");
    }
};

// Récupérer la liste des commerciaux
const getCommercials = async () => {
    try {
        const { items } = await pb.collection('users').getList(1, 50, {
            filter: 'role ~ "Commercial"',
            sort: 'name',
        });
        return items;
    } catch (error) {
        // Retourner un tableau vide en cas d'erreur pour éviter de casser l'application
        return [];
    }
};

// Assigner un commercial à une offre d'alternance
const assignCommercialToOffer = async (offerId, commercialId) => {
    try {
        const record = await pb.collection('alternance_offers').update(offerId, {
            assigned_to: commercialId,
        });
        eventEmitter.dispatch('offersUpdated');
        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de l'assignation du commercial à l'offre.");
    }
};

// Ajouter un prospect à une offre d'alternance
const addProspectToOffer = async (offerId, prospectId) => {
    try {
        const currentOffer = await pb.collection('alternance_offers').getOne(offerId);
        const existingCandidates = currentOffer.candidats_ids || [];

        if (existingCandidates.includes(prospectId)) {
            return { success: false, error: 'Ce prospect est déjà candidat à cette offre.' };
        }

        const updatedCandidates = [...existingCandidates, prospectId];
        const record = await pb.collection('alternance_offers').update(offerId, {
            candidats_ids: updatedCandidates,
        });

        eventBus.emit(EVENTS.PROSPECT_ADDED_TO_OFFER, { offerId, prospectId, updatedCandidates });

        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de l'ajout du prospect à l'offre.");
    }
};

// Retirer un prospect d'une offre d'alternance
const removeProspectFromOffer = async (offerId, prospectId) => {
    try {
        const currentOffer = await pb.collection('alternance_offers').getOne(offerId);
        const existingCandidates = currentOffer.candidats_ids || [];

        if (!existingCandidates.includes(prospectId)) {
            return { success: false, error: "Ce prospect n'est pas candidat à cette offre." };
        }

        const updatedCandidates = existingCandidates.filter(id => id !== prospectId);
        const record = await pb.collection('alternance_offers').update(offerId, {
            candidats_ids: updatedCandidates,
        });

        eventBus.emit(EVENTS.PROSPECT_REMOVED_FROM_OFFER, { offerId, prospectId, updatedCandidates });

        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la suppression du prospect de l'offre.");
    }
};

// Compter les offres d'emploi non assignées
const getUnassignedOffersCount = async () => {
    try {
        const { totalItems } = await pb.collection('alternance_offers').getList(1, 1, {
            filter: 'assigned_to = null',
        });
        return totalItems;
    } catch (error) {
        // Retourner 0 en cas d'erreur
        return 0;
    }
};

// Récupérer les candidats d'une offre d'alternance
const getOfferCandidates = async (offerId) => {
    try {
        const offer = await pb.collection('alternance_offers').getOne(offerId);

        if (!offer.candidats_ids || offer.candidats_ids.length === 0) {
            return { success: true, data: [] };
        }

        const candidates = await Promise.all(
            offer.candidats_ids.map(id =>
                pb.collection('students').getOne(id, { expand: 'formation,company' })
                    .catch(err => {
                        console.error(`Erreur lors de la récupération du candidat ${id}:`, err);
                        return null;
                    })
            )
        );

        const validCandidates = candidates.filter(c => c !== null);

        return { success: true, data: validCandidates };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la récupération des candidats de l'offre.");
    }
};

export {
    createOffer,
    getCompanyOffers,
    getOfferById,
    updateOffer,
    deleteOffer,
    deleteMultipleOffers,
    getCommercials,
    assignCommercialToOffer,
    addProspectToOffer,
    removeProspectFromOffer,
    getUnassignedOffersCount,
    getOfferCandidates,
};